import React, { useEffect } from 'react';

// eslint-disable-next-line no-restricted-imports
import { useLocation, useNavigate } from 'react-router';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { useInject } from '@vk-hr-tek/core/ioc';
import { Link } from '@vk-hr-tek/ui/Link';
import { Logger } from '@vk-hr-tek/core/logger';
import { showNotification } from '@vk-hr-tek/core/notifications';
import { CopyButton } from '@vk-hr-tek/ui/CopyButton';
import { Button } from '@vk-hr-tek/ui/Button';
import { useIsDesktop } from '@vk-hr-tek/ui/hooks';

import { useDispatch } from '@app/hooks';

import { DEFAULT_PAGE_ERROR_CONFIG, PAGE_ERROR_CONFIG } from '../constants';

interface PageErrorProps {
  status: number;
  errorMessage?: string;
  traceId?: string | null;
}

export const PageError = ({
  status,
  errorMessage,
  traceId,
}: PageErrorProps) => {
  const logger = useInject(Logger);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();

  const config = PAGE_ERROR_CONFIG[status] ?? DEFAULT_PAGE_ERROR_CONFIG;
  const ImageComponent = config.image;

  const caption = config.ignoreErrorMessage
    ? config.caption
    : errorMessage || config.caption;

  const supportMail = window.REACT_APP_VKHRTEK_SUPPORT_MAIL;
  const showMailBlock = config.showSupport && supportMail;
  const showTraceId = config.showSupport && traceId;
  const showBack = config.showBack && location.key !== 'default';
  const showReload = config.showReload && isDesktop;

  const handleGoMainPage = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleReload = () => window.location.reload();

  const handleMailTo = () => {
    window.location.href = `mailto:${supportMail}`;
  };

  useEffect(() => {
    logger.info(`[ui] [${status}] GET ${location.pathname}`, {
      tags: {
        vkdoc_error_type: 'ui',
        http_status: status,
        http_trace_id: traceId || 'empty',
        app_url: location.pathname,
      },
      context: {
        app_error_message: {
          value: errorMessage || config.caption,
        },
        ...(location.search
          ? {
              app_query: {
                search: location.search,
                hash: location.hash || null,
              },
            }
          : {}),
      },
    });
  }, [logger, errorMessage, location, status, config.caption, traceId]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      height="100%"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={{ xs: 330, md: 340 }}
      >
        <ImageComponent />

        <Box
          display="flex"
          gap="8"
          flexDirection="column"
          textAlign="center"
          mt="20"
        >
          <Typography variant="h6">{config.title}</Typography>
          <Typography variant="body3" color="text.light.secondary">
            {caption}
          </Typography>
          {showMailBlock && (
            <Typography variant="body3" color="text.light.secondary">
              {`Если ошибка повторяется, напишите письмо ${
                traceId ? 'с кодом ошибки' : ''
              } на `}
              <Link size="small" variant="simple" onClick={handleMailTo}>
                {supportMail}
              </Link>
            </Typography>
          )}
          {!!window.REACT_APP_VKHRTEK_STATUS_PAGE_LINK &&
            config.showStatusPage && (
              <Typography variant="body3" color="text.light.secondary">
                {`Актуальную информацию о ходе работ публикуем на `}
                <Link
                  variant="simple"
                  size="small"
                  href={window.REACT_APP_VKHRTEK_STATUS_PAGE_LINK}
                  target="_blank"
                >
                  странице статуса системы
                </Link>
              </Typography>
            )}
        </Box>

        {showTraceId && (
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            mt="16"
            gap="8"
            justifyContent="space-between"
          >
            <Typography noWrap color="text.light.secondary" variant="body3">
              <Box display="flex" gap="8">
                <Box flexShrink={0}>Код ошибки:</Box>
                <Box
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  component="span"
                >
                  {traceId}
                </Box>
              </Box>
            </Typography>
            <CopyButton
              valueToCopy={traceId}
              onClick={() =>
                dispatch(showNotification('Код ошибки скопирован'))
              }
              size="16"
            />
          </Box>
        )}
        <Box width="100%" display="flex" flexDirection="column" gap="8" mt="20">
          {config.showHistory && (
            <>
              <Button
                onClick={handleGoMainPage}
                variant="primary"
                size="small"
                fullWidth
              >
                Вернуться на главную
              </Button>
              {showBack && (
                <Button
                  onClick={handleGoBack}
                  variant="quaternary"
                  size="small"
                  fullWidth
                >
                  Назад
                </Button>
              )}
            </>
          )}
          {showReload && (
            <Button
              onClick={handleReload}
              variant="quaternary"
              size="small"
              fullWidth
            >
              Обновить страницу
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
